<template>
    <tr :class="loyer.new ? 'bg-indigo-100 rounded-md' : 'border-t border-indigo-300'">
        <td class="px-2 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
            <p class="flex items-center">
                {{ formatMonth(loyer.month) }} {{ loyer.year }}
                <span v-if="loyer.new" class="ml-2 bg-indigo-600 text-white rounded-lg px-2 py-1 text-xs">New</span>
            </p>
            <p v-if="loyer.paiement_date" class="text-indigo-400 italic text-xs">
                Payé le {{ formatDate(loyer.paiement_date) }}
            </p>
            <p v-if="loyer.periode_debut && loyer.periode_fin" class="text-orange-500 italic text-xs">
                Prorata du {{ formatDate(loyer.periode_debut) }} au {{ formatDate(loyer.periode_fin) }}
            </p>
        </td>
        <td class="px-2 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
            <p>
                {{ loyer.loyer+loyer.charges }} €
            </p>
            <p v-if="loyer.charges" class="text-indigo-400 italic text-xs">
                {{ loyer.loyer }}  + {{ loyer.charges }} € de charges
            </p>
        </td>
        <td class="px-2 py-2 text-sm text-gray-700 dark:text-gray-300 flex flex-col">
            <HabitantRel :habitant="habitant" :locataireUuid="habitant?.locataire?.uuid" v-for="habitant in loyer.habitants" :key="habitant.uuid" class="mt-1"/>
        </td>
        <td class="px-2 py-2 text-sm whitespace-nowrap">
            <span
                class="flex items-center justify-end"
            >
                <button v-if="loyer.new" class="btn-success mr-1" @click.prevent="openValidateLoyerModal(loyer.uuid);">
                    <font-awesome-icon class="text-sm" :icon="['fa', 'check']" />
                </button>
                <button v-if="loyer.quittances && loyer.quittances.length > 0" class="btn-info mr-1" @click.prevent="$refs.listQuittancesModal.openModal(loyer.quittances)">
                    <font-awesome-icon class="text-sm fa-w-18" :icon="['fa', 'file']" />
                </button>
                <button class="btn-secondary mr-1" @click="$refs.editLoyerModal.openModal(loyer)">
                    <font-awesome-icon class="text-sm" :icon="['fa', 'edit']" />
                </button>
                <button class="btn-danger px-4" @click.prevent="confirmRemoveLoyer(loyer.uuid);">
                    <font-awesome-icon class="text-sm text-white" :icon="['fa', 'times']" />
                </button>
            </span>
        </td>
    </tr>
    <EditLoyerModal
        ref="editLoyerModal"
        @handleUpdateList="$emit('refresh')"
        :bien="bien"
    />
    <ListQuittancesModal
        ref="listQuittancesModal"
        :loyerUuid="loyer.uuid"
    />
    <PrimaryModal
        title="Valider le loyer"
        ref="validateLoyerModal"
        btnSuccessText="Valider"
        classButton="btn-success"
        @ok="validateLoyer()"
    >
        <template v-slot:content>
            <p class="text-sm">
                Voulez-vous vraiment valider ce loyer créé automatiquement et l'intégrer.
            </p>
            <div class="w-full md:w-1/2">
                <DatePicker
                    v-model='paiementDate'
                    class="mt-3 mr-2"
                    title="Date de paiement"
                    id='paiementDate'
                />
            </div>
        </template>
        <template v-slot:footer>
            <div
                class="mr-2" 
            >
                <SwitchForm labelRight labelInline v-model='sendRentReceip' title="Envoyer la quittance de loyer par email" id='switchSendRentReceip' class="my-3"/>
            </div>
        </template>
    </PrimaryModal>

</template>

<script>

import { inject } from 'vue';
import HabitantRel from './../../../composant/habitant/HabitantRel.vue';
import PrimaryModal from './../../../composant/PrimaryModal';
import SwitchForm from './../../../form/SwitchForm';
import DatePicker from './../../../form/DatePicker';
import EditLoyerModal from './../../../modal/bien/financials/loyer/EditLoyerModal';
import ListQuittancesModal from './../../../modal/bien/financials/loyer/ListQuittancesModal';

export default {
    components: {
        HabitantRel,
        SwitchForm,
        PrimaryModal,
        EditLoyerModal,
        ListQuittancesModal,
        DatePicker
    },

    emits: ['refresh'],

    data() {
        return {
            openLoyer: false,
            loyerUuid: null,
            editLoyerData: {},
            sendRentReceip: 0,
            paiementDate: null
        }
    },

    props: {
        loyer: {
            type: Object,
            default() {
                return {}
            }
        },
        bien: {
            type: Object,
            default: null
        },
    },

    mounted() {
        const tokenServices = inject('tokenServices')

        this.sendRentReceip = tokenServices.getUserSettingFromKey('default_send_quittance_email') === true ? 1 : 0
        this.paiementDate = this.loyer.paiement_date ? new Date(this.loyer.paiement_date) : new Date()
    },

    methods: {
        openValidateLoyerModal(loyerUuid) {
            this.loyerUuid = loyerUuid
            this.$refs.validateLoyerModal.openModal()
        },

        confirmRemoveLoyer(loyerUuid) {
            this.$confirm({
                variant: 'danger',
                title: 'Supprimer le loyer',
                message: "Vous êtes sur le point de supprimer ce loyer. Cette action est irréversible, en êtes-vous sûr ?",
                confirmText: 'Supprimer',
                action: () => this.removeLoyer(loyerUuid),
            });
        },

        async removeLoyer(loyerUuid) {
            if (!this.$route.params.bienUuid || !loyerUuid) {
                return;
            }

            await this.$api.bien.removeLoyer(this.$route.params.bienUuid, loyerUuid)
            .then(async () => {
                this.$toast({title: 'Succès', description: "Le loyer à bien été supprimé."}, this.$toastSuccess);
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.$emit('refresh')
        },

        async validateLoyer() {
            if (!this.$route.params.bienUuid || !this.loyerUuid || !this.paiementDate) {
                return;
            }

            await this.$api.bien.validateLoyer(
                this.$route.params.bienUuid,
                this.loyerUuid,
                {
                    paiement_date: this.paiementDate.toISOString()
                },
                this.sendRentReceip)
            .then(async () => {
                this.$toast({title: 'Succès', description: "Le loyer à bien été validé."}, this.$toastSuccess);
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.$emit('refresh')
            this.$refs.validateLoyerModal.dismissModal()
        }
    }
};
</script>
